import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  COL, isCommissionFinal, type Commission, type CommissionMessage, type CommissionStatus,
} from '@eprintsmart/shared';
import { db } from '../firebase';

/** Commissioned design work, from the staff side. */

const fns = () => getFunctions(undefined, 'asia-south1');

/**
 * Every commission, with the ones needing attention first.
 *
 * Sorted so open engagements lead and, within those, the ones idle longest —
 * which is exactly the order somebody working through this should meet them.
 * Done client-side because it is a two-key sort Firestore would need a
 * composite index for, over a collection measured in tens.
 */
export function watchCommissions(cb: (rows: Commission[]) => void, max = 200): () => void {
  return onSnapshot(
    query(collection(db, COL.commissions), orderBy('updatedAt', 'desc'), limit(max)),
    (snap) => {
      const rows = snap.docs.map((d) => ({ ...(d.data() as Commission), id: d.id }));
      rows.sort((a, b) => {
        const aOpen = isCommissionFinal(a.status) ? 1 : 0;
        const bOpen = isCommissionFinal(b.status) ? 1 : 0;
        if (aOpen !== bOpen) return aOpen - bOpen;
        return Number(a.updatedAt) - Number(b.updatedAt);
      });
      cb(rows);
    },
  );
}

export function watchCommissionMessages(
  commissionId: string,
  cb: (rows: CommissionMessage[]) => void,
): () => void {
  return onSnapshot(
    query(collection(db, COL.commissions, commissionId, 'messages'), orderBy('at', 'asc')),
    (snap) => cb(snap.docs.map((d) => d.data() as CommissionMessage)),
  );
}

/** Posts as `staff`, which the server stamps — the label is not ours to choose
 *  and appears as ePrintSmart to both parties. */
export async function postCommissionMessage(payload: {
  commissionId: string;
  body: string;
}): Promise<void> {
  await httpsCallable(fns(), 'postCommissionMessage')(payload);
}

export async function transitionCommission(payload: {
  commissionId: string;
  to: CommissionStatus;
  note?: string;
}): Promise<void> {
  await httpsCallable(fns(), 'transitionCommission')(payload);
}
